import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, TrendingUp, TrendingDown } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import { todayCR } from "../utils/dateHelpers";

// Removed "venta" and "premio" from OTHER_TRANSACTIONS
const OTHER_TRANSACTION_TYPES = [
  { value: "sinpe", label: "SINPE Móvil", color: "bg-blue-100 text-blue-700" },
  { value: "banco_tucan", label: "Banco Tucán", color: "bg-purple-100 text-purple-700" },
  { value: "banco_payser", label: "Banco Payser", color: "bg-orange-100 text-orange-700" },
  { value: "banco_western", label: "Western Union", color: "bg-yellow-100 text-yellow-700" },
  { value: "banco_rapibac", label: "Rapibac", color: "bg-pink-100 text-pink-700" },
  { value: "banco_otro", label: "Otro Banco", color: "bg-gray-100 text-gray-700" },
  { value: "entrada", label: "Entrada de Dinero", color: "bg-emerald-100 text-emerald-700" },
  { value: "salida", label: "Salida de Dinero", color: "bg-rose-100 text-rose-700" },
  { value: "salida_premios", label: "Salida Premios", color: "bg-red-200 text-red-800" },
  { value: "pago_dias_anteriores_afecta", label: "Pago Días Anteriores (Afecta)", color: "bg-cyan-100 text-cyan-700" },
  { value: "pago_dias_anteriores_no_afecta", label: "Pago Días Anteriores (NO Afecta)", color: "bg-cyan-50 text-cyan-600" },
  { value: "no_afecta_caja", label: "No Afecta Caja", color: "bg-slate-100 text-slate-700" },
];

const ALL_TRANSACTION_TYPES = [
  { value: "venta", label: "Venta (Efectivo)", color: "bg-green-100 text-green-700" },
  { value: "premio", label: "Premio (Salida)", color: "bg-red-100 text-red-700" },
  ...OTHER_TRANSACTION_TYPES
];

import { useBox } from "../components/Layout";

export default function Transactions({ user }) {
  const { selectedBox } = useBox();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  
  // Commission configuration
  const [commissionConfig, setCommissionConfig] = useState({
    venta_macho_commission: 7.5,
    venta_tiro_commission: 7.5
  });
  
  // Quick entry for ventas and premios - persisted in backend
  const [quickEntry, setQuickEntry] = useState({
    venta_macho: 0,
    premio_macho: 0,
    venta_tiro: 0,
    premio_tiro: 0
  });
  const [pendingValuesLoaded, setPendingValuesLoaded] = useState(false);

  // Fetch pending values from backend
  const fetchPendingValues = async () => {
    if (!selectedBox) return;
    try {
      const response = await axiosInstance.get(`/pending-values?box_id=${selectedBox.id}`);
      if (response.data.success && response.data.pending_values) {
        setQuickEntry({
          venta_macho: response.data.pending_values.venta_macho || 0,
          premio_macho: response.data.pending_values.premio_macho || 0,
          venta_tiro: response.data.pending_values.venta_tiro || 0,
          premio_tiro: response.data.pending_values.premio_tiro || 0
        });
      }
      setPendingValuesLoaded(true);
    } catch (error) {
      console.error("Error fetching pending values:", error);
      setPendingValuesLoaded(true);
    }
  };

  // Save pending values to backend whenever quickEntry changes
  const savePendingValues = async (values) => {
    if (!selectedBox || !pendingValuesLoaded) return;
    try {
      await axiosInstance.post('/pending-values', {
        box_id: selectedBox.id,
        ...values
      });
    } catch (error) {
      console.error("Error saving pending values:", error);
    }
  };

  // Debounced save to avoid too many API calls
  useEffect(() => {
    if (!pendingValuesLoaded) return;
    const timeoutId = setTimeout(() => {
      savePendingValues(quickEntry);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [quickEntry, pendingValuesLoaded]);
  
  const [formData, setFormData] = useState({
    type: "sinpe",
    amount_colones: 0,
    amount_dollars: 0,
    description: ""
  });

  const fetchTransactions = async () => {
    try {
      const url = selectedBox ? `/transactions?box_id=${selectedBox.id}` : '/transactions';
      const response = await axiosInstance.get(url);
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissionConfig = async () => {
    if (!selectedBox) return;
    
    try {
      const response = await axiosInstance.get(`/commission-config/${selectedBox.id}`);
      setCommissionConfig({
        venta_macho_commission: response.data.venta_macho_commission,
        venta_tiro_commission: response.data.venta_tiro_commission
      });
    } catch (error) {
      console.error("Error fetching commission config:", error);
    }
  };

  const updateCommissionConfig = async () => {
    if (!selectedBox) return;
    
    try {
      await axiosInstance.patch(`/commission-config/${selectedBox.id}`, null, {
        params: {
          venta_macho_commission: commissionConfig.venta_macho_commission,
          venta_tiro_commission: commissionConfig.venta_tiro_commission
        }
      });
      toast.success("Porcentajes de comisión actualizados");
      setShowCommissionDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al actualizar comisiones"));
    }
  };

  useEffect(() => {
    if (selectedBox) {
      fetchTransactions();
      fetchCommissionConfig();
      fetchPendingValues();  // Load pending values from backend
    }
  }, [selectedBox]);

  // Handle quick entry for ventas and premios
  const handleQuickEntry = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }

    try {
      const venta_macho = parseFloat(quickEntry.venta_macho) || 0;
      const venta_tiro = parseFloat(quickEntry.venta_tiro) || 0;
      const venta_total = venta_macho + venta_tiro;
      
      const premio_total = parseFloat(quickEntry.premio_macho) + parseFloat(quickEntry.premio_tiro);

      // Create venta transaction if total > 0
      if (venta_total > 0) {
        const venta_macho_commission = venta_macho * (commissionConfig.venta_macho_commission / 100);
        const venta_tiro_commission = venta_tiro * (commissionConfig.venta_tiro_commission / 100);
        
        await axiosInstance.post(`/transactions?box_id=${selectedBox.id}`, {
          type: "venta",
          amount_colones: venta_total,
          amount_dollars: 0,
          description: `VENTA MACHO: ₡${venta_macho} (${commissionConfig.venta_macho_commission}%) + VENTA TIRO: ₡${venta_tiro} (${commissionConfig.venta_tiro_commission}%)`,
          venta_macho_amount: venta_macho,
          venta_tiro_amount: venta_tiro,
          venta_macho_commission_pct: commissionConfig.venta_macho_commission,
          venta_tiro_commission_pct: commissionConfig.venta_tiro_commission,
          venta_macho_commission: venta_macho_commission,
          venta_tiro_commission: venta_tiro_commission
        });
      }

      // Create premio transaction if total > 0
      if (premio_total > 0) {
        await axiosInstance.post(`/transactions?box_id=${selectedBox.id}`, {
          type: "premio",
          amount_colones: premio_total,
          amount_dollars: 0,
          description: `PREMIO MACHO: ₡${quickEntry.premio_macho} + PREMIO TIRO: ₡${quickEntry.premio_tiro}`
        });
      }

      if (venta_total > 0 || premio_total > 0) {
        toast.success("Ventas y premios registrados exitosamente");
        const resetValues = { venta_macho: 0, premio_macho: 0, venta_tiro: 0, premio_tiro: 0 };
        setQuickEntry(resetValues);
        // Clear pending values from backend
        try {
          await axiosInstance.delete(`/pending-values?box_id=${selectedBox.id}`);
        } catch (e) {
          console.error("Error clearing pending values:", e);
        }
        fetchTransactions();
      } else {
        toast.error("Ingresa al menos un monto");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar"));
    }
  };

  const handleCreateTransaction = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    try {
      await axiosInstance.post(`/transactions?box_id=${selectedBox.id}`, formData);
      toast.success("Movimiento registrado exitosamente");
      setShowDialog(false);
      setFormData({ type: "venta", amount_colones: 0, amount_dollars: 0, description: "" });
      fetchTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar movimiento"));
    }
  };

  const handleDeleteTransaction = async () => {
    if (!selectedTransaction) return;
    
    if (!deletePassword.trim()) {
      toast.error("Ingresa la contraseña master para confirmar");
      return;
    }
    
    try {
      // Enviar password como query string en la URL
      const encodedPassword = encodeURIComponent(deletePassword);
      const response = await axiosInstance.delete(
        `/transactions/${selectedTransaction.id}?password=${encodedPassword}`
      );
      toast.success(response.data.message || "Movimiento eliminado y efectos revertidos correctamente");
      setShowDeleteDialog(false);
      setSelectedTransaction(null);
      setDeletePassword("");
      fetchTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar movimiento"));
    }
  };

  const getTypeInfo = (type) => {
    return ALL_TRANSACTION_TYPES.find(t => t.value === type) || ALL_TRANSACTION_TYPES[0];
  };

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };
  
  const filterTransactionsByDate = () => {
    if (!selectedDate) return transactions;
    
    return transactions.filter(t => {
      const transactionDate = new Date(t.created_at).toISOString().split('T')[0];
      return transactionDate === selectedDate;
    });
  };
  
  const displayedTransactions = filterTransactionsByDate();
  
  const getTodayDate = () => {
    return todayCR();
  };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Movimientos
          </h1>
          <p className="text-slate-600">Registra ventas, premios y pagos</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setShowDateFilter(!showDateFilter);
              if (!showDateFilter) {
                setSelectedDate(getTodayDate());
              } else {
                setSelectedDate("");
              }
            }}
            variant={showDateFilter ? "default" : "outline"}
            className="gap-2"
          >
            📅 {showDateFilter ? "Ver Todos" : "Filtrar por Fecha"}
          </Button>
          
          <Button
            onClick={() => {
              setSelectedDate(getTodayDate());
              setShowDateFilter(true);
            }}
            variant="outline"
            className="gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700"
          >
            🕐 Ver Hoy
          </Button>
        </div>
      </div>
      
      {showDateFilter && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label htmlFor="date-filter" className="whitespace-nowrap font-semibold">
                Filtrar por fecha:
              </Label>
              <Input
                id="date-filter"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="max-w-xs"
              />
              <div className="text-sm text-blue-700 font-medium">
                Mostrando {displayedTransactions.length} de {transactions.length} movimientos
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT PANEL - Quick Entry for Ventas and Premios */}
        <div className="lg:col-span-1 space-y-4">
          {/* Ventas Section */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-green-700">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Ventas en Efectivo
                </div>
                {user?.role === 'master' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCommissionDialog(true)}
                    className="text-xs"
                  >
                    Editar %
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-green-700">VENTA MACHO</Label>
                  <span className="text-xs text-green-600 font-semibold">{commissionConfig.venta_macho_commission}%</span>
                </div>
                <Input
                  type="number"
                  value={quickEntry.venta_macho}
                  onChange={(e) => setQuickEntry({...quickEntry, venta_macho: e.target.value})}
                  placeholder="0"
                  className="border-green-300"
                />
                <p className="text-xs text-green-600 mt-1">
                  Comisión: ₡{((parseFloat(quickEntry.venta_macho) || 0) * (commissionConfig.venta_macho_commission / 100)).toFixed(2)}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-green-700">VENTA TIRO</Label>
                  <span className="text-xs text-green-600 font-semibold">{commissionConfig.venta_tiro_commission}%</span>
                </div>
                <Input
                  type="number"
                  value={quickEntry.venta_tiro}
                  onChange={(e) => setQuickEntry({...quickEntry, venta_tiro: e.target.value})}
                  placeholder="0"
                  className="border-green-300"
                />
                <p className="text-xs text-green-600 mt-1">
                  Comisión: ₡{((parseFloat(quickEntry.venta_tiro) || 0) * (commissionConfig.venta_tiro_commission / 100)).toFixed(2)}
                </p>
              </div>
              <div className="pt-2 border-t border-green-300">
                <div className="flex justify-between text-sm font-semibold text-green-700">
                  <span>Total Ventas:</span>
                  <span>₡{(parseFloat(quickEntry.venta_macho) + parseFloat(quickEntry.venta_tiro)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-green-600 mt-1">
                  <span>Total Comisiones:</span>
                  <span>₡{(((parseFloat(quickEntry.venta_macho) || 0) * (commissionConfig.venta_macho_commission / 100)) + 
                             ((parseFloat(quickEntry.venta_tiro) || 0) * (commissionConfig.venta_tiro_commission / 100))).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Premios Section */}
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <TrendingDown className="w-5 h-5" />
                Premios (Salida)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-red-700">PREMIO MACHO</Label>
                <Input
                  type="number"
                  value={quickEntry.premio_macho}
                  onChange={(e) => setQuickEntry({...quickEntry, premio_macho: e.target.value})}
                  placeholder="0"
                  className="border-red-300"
                />
              </div>
              <div>
                <Label className="text-red-700">PREMIO TIRO</Label>
                <Input
                  type="number"
                  value={quickEntry.premio_tiro}
                  onChange={(e) => setQuickEntry({...quickEntry, premio_tiro: e.target.value})}
                  placeholder="0"
                  className="border-red-300"
                />
              </div>
              <div className="pt-2 border-t border-red-300">
                <div className="flex justify-between text-sm font-semibold text-red-700">
                  <span>Total Premios:</span>
                  <span>₡{(parseFloat(quickEntry.premio_macho) + parseFloat(quickEntry.premio_tiro)).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            onClick={handleQuickEntry}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            Guardar Ventas y Premios
          </Button>
        </div>

        {/* RIGHT PANEL - Transactions List and Other Movements */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-end">
            <Button
              data-testid="add-transaction-button"
              onClick={() => setShowDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              <Plus className="w-4 h-4" />
              Otro Movimiento
            </Button>
          </div>

      <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle>Historial de Movimientos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {displayedTransactions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                {showDateFilter 
                  ? `No hay movimientos para la fecha seleccionada (${selectedDate})` 
                  : "No hay movimientos registrados"}
              </p>
            ) : (
              displayedTransactions.map((transaction) => {
                const typeInfo = getTypeInfo(transaction.type);
                const isVoided = transaction.voided === true;
                return (
                  <div
                    key={transaction.id}
                    data-testid={`transaction-${transaction.id}`}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isVoided
                        ? "bg-red-50/50 border-red-200 opacity-70 line-through decoration-red-500 decoration-2"
                        : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                        {isVoided && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-600 text-white no-underline" data-testid={`voided-badge-${transaction.id}`}>
                            Eliminado
                          </span>
                        )}
                        <span className="text-xs text-slate-500 no-underline">
                          {new Date(transaction.created_at).toLocaleString('es-CR')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{transaction.description}</p>
                      <p className="text-xs text-slate-500 mt-1 no-underline">Por: {transaction.created_by}</p>
                      {isVoided && transaction.voided_at && (
                        <p className="text-[11px] text-red-700 mt-0.5 no-underline">
                          Anulado {new Date(transaction.voided_at).toLocaleString('es-CR')} por {transaction.voided_by || "—"}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-slate-800">
                          {formatCurrency(transaction.amount_colones)}
                        </p>
                        <p className="text-sm text-slate-600">
                          {formatCurrency(transaction.amount_dollars, "dollars")}
                        </p>
                      </div>
                      
                      {user.role === 'master' && !isVoided && (
                        transaction.register_closed ? (
                          <Button
                            data-testid={`delete-transaction-${transaction.id}-locked`}
                            variant="ghost"
                            size="icon"
                            disabled
                            title="Caja cerrada — revertir cierre para eliminar"
                            className="text-slate-300 cursor-not-allowed no-underline"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            data-testid={`delete-transaction-${transaction.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setShowDeleteDialog(true);
                            }}
                            className="hover:bg-red-50 hover:text-red-600 no-underline"
                            title="Eliminar movimiento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo Movimiento</DialogTitle>
            <DialogDescription>Registra una nueva transacción</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="type">Tipo de Movimiento</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({...formData, type: value})}
              >
                <SelectTrigger data-testid="transaction-type-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OTHER_TRANSACTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="amount_colones">Monto en Colones</Label>
              <Input
                id="amount_colones"
                data-testid="transaction-colones-input"
                type="number"
                step="0.01"
                value={formData.amount_colones}
                onChange={(e) => setFormData({...formData, amount_colones: parseFloat(e.target.value) || 0})}
                className="mt-1.5"
              />
            </div>
            
            {selectedBox?.uses_dollars !== false && (
              <div>
                <Label htmlFor="amount_dollars">Monto en Dólares</Label>
                <Input
                  id="amount_dollars"
                  data-testid="transaction-dollars-input"
                  type="number"
                  step="0.01"
                  value={formData.amount_dollars}
                  onChange={(e) => setFormData({...formData, amount_dollars: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                data-testid="transaction-description-input"
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1.5"
                placeholder="Detalles del movimiento"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-transaction-button"
              onClick={handleCreateTransaction}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-transaction-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar Movimiento y Revertir Efectos</DialogTitle>
            <DialogDescription>
              Al eliminar este movimiento, se revertirán automáticamente todos sus efectos:
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-3 py-4">
              <div className="bg-slate-50 p-3 rounded-lg space-y-2">
                <p className="text-sm font-medium">Movimiento a eliminar:</p>
                <p className="text-sm text-slate-600">
                  <strong>Tipo:</strong> {getTypeInfo(selectedTransaction.type).label}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Monto:</strong> {formatCurrency(selectedTransaction.amount_colones)} / {formatCurrency(selectedTransaction.amount_dollars, "dollars")}
                </p>
                {selectedTransaction.description && (
                  <p className="text-sm text-slate-600">
                    <strong>Descripción:</strong> {selectedTransaction.description}
                  </p>
                )}
              </div>
              
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Efectos que se revertirán:</p>
                <ul className="text-xs text-amber-700 space-y-1 ml-4 list-disc">
                  <li>Se actualizarán los totales de la caja registradora</li>
                  <li>Si está relacionado con una cuenta, se revertirán los cambios</li>
                  <li>Si es un pago, el monto se devolverá a la deuda</li>
                  <li>Si es un aumento, el monto se reducirá de la deuda</li>
                </ul>
              </div>
              
              <div>
                <Label htmlFor="delete_password">Contraseña Master (Obligatoria)</Label>
                <Input
                  id="delete_password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="mt-1.5"
                  placeholder="Ingresa tu contraseña master"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeletePassword("");
              setSelectedTransaction(null);
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-transaction-button"
              onClick={handleDeleteTransaction}
              variant="destructive"
            >
              Eliminar y Revertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Config Dialog - Only for Master */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Porcentajes de Comisión</DialogTitle>
            <DialogDescription>
              Ajusta los porcentajes de comisión para VENTA MACHO y VENTA TIRO
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="macho_pct">Comisión VENTA MACHO (%)</Label>
              <Input
                id="macho_pct"
                type="number"
                step="0.1"
                value={commissionConfig.venta_macho_commission}
                onChange={(e) => setCommissionConfig({...commissionConfig, venta_macho_commission: parseFloat(e.target.value)})}
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label htmlFor="tiro_pct">Comisión VENTA TIRO (%)</Label>
              <Input
                id="tiro_pct"
                type="number"
                step="0.1"
                value={commissionConfig.venta_tiro_commission}
                onChange={(e) => setCommissionConfig({...commissionConfig, venta_tiro_commission: parseFloat(e.target.value)})}
                className="mt-1.5"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={updateCommissionConfig}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </div>
      </div>
    </div>
  );
}